/**
 * Correlation-id helpers.
 *
 * Per `plans/03-architecture/observability.md`, every Worker/Pages/extension
 * request carries an `X-Correlation-Id` header. If absent at the edge we
 * mint a fresh UUID v7 (time-ordered) and propagate it through Hono context,
 * AI Gateway metadata, log lines, and Sentry tags.
 *
 * Boundary handling mirrors `@barel/i18n/server`'s `negotiateLocale`:
 * we accept a `Request`, a bare `Headers`, or a plain `Record<string,string>`.
 */
const HEADER = 'X-Correlation-Id';
const HEADER_LC = 'x-correlation-id';
function getCrypto() {
    const c = globalThis.crypto;
    if (!c || typeof c.getRandomValues !== 'function') {
        throw new Error('@barel/telemetry: globalThis.crypto.getRandomValues is required');
    }
    return c;
}
/**
 * Mint a UUID v7 string (RFC 9562). Time-ordered: the first 48 bits are
 * the Unix timestamp in milliseconds, followed by random data.
 *
 * Browsers, Workers, Node 20+, and Vitest all expose `crypto.getRandomValues`.
 */
export function mintCorrelationId() {
    const bytes = new Uint8Array(16);
    getCrypto().getRandomValues(bytes);
    const ms = BigInt(Date.now());
    // 48-bit timestamp (big-endian) into bytes[0..5]
    bytes[0] = Number((ms >> 40n) & 0xffn);
    bytes[1] = Number((ms >> 32n) & 0xffn);
    bytes[2] = Number((ms >> 24n) & 0xffn);
    bytes[3] = Number((ms >> 16n) & 0xffn);
    bytes[4] = Number((ms >> 8n) & 0xffn);
    bytes[5] = Number(ms & 0xffn);
    // Set version (7) in the top nibble of byte 6
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
    // Set variant (10xxxxxx) in the top two bits of byte 8
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (`${hex[0]}${hex[1]}${hex[2]}${hex[3]}-` +
        `${hex[4]}${hex[5]}-` +
        `${hex[6]}${hex[7]}-` +
        `${hex[8]}${hex[9]}-` +
        `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`);
}
function readFromHeaders(headers) {
    return headers.get(HEADER) ?? headers.get(HEADER_LC);
}
function readFromRecord(record) {
    // Headers can be reported with any casing; do a case-insensitive sweep.
    if (typeof record[HEADER] === 'string' && record[HEADER])
        return record[HEADER];
    if (typeof record[HEADER_LC] === 'string' && record[HEADER_LC])
        return record[HEADER_LC];
    for (const [k, v] of Object.entries(record)) {
        if (typeof v === 'string' && v && k.toLowerCase() === HEADER_LC) {
            return v;
        }
    }
    return null;
}
function readHeader(source) {
    if (source instanceof Request) {
        return readFromHeaders(source.headers);
    }
    if (source instanceof Headers) {
        return readFromHeaders(source);
    }
    // Tagged objects with a `headers` slot (e.g., Hono Context.req.raw).
    const maybeHeaders = source
        .headers;
    if (maybeHeaders instanceof Headers) {
        return readFromHeaders(maybeHeaders);
    }
    if (maybeHeaders && typeof maybeHeaders === 'object') {
        return readFromRecord(maybeHeaders);
    }
    if (typeof source === 'object' && source !== null) {
        return readFromRecord(source);
    }
    return null;
}
/**
 * Read `X-Correlation-Id` from any boundary object; mint a UUID v7 if absent.
 * Idempotent, side-effect-free — callers are responsible for stashing the
 * returned id back into context (`c.set('correlationId', id)`).
 */
export function getOrMintCorrelationId(source) {
    const incoming = readHeader(source);
    if (incoming && incoming.trim().length > 0) {
        return incoming.trim();
    }
    return mintCorrelationId();
}
/** Header name the rest of the stack should use when forwarding the id. */
export const CORRELATION_HEADER = HEADER;
//# sourceMappingURL=correlation.js.map